import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { fetchRequestResponseData } from "utils/commonFunctions";

interface SimFlowData {
  stepID: number;
  stepName: string;
  data: any[];
}

export interface SimFlowState {
  data: SimFlowData[];
  activeStep: number;
  flowId: number;
  loading: boolean;
  error: any;
}

const simFlowInitialState: SimFlowState | null = {
  data: [
    {
      stepID: 9,
      stepName: "Order Change Response From Sim Engine",
      data: [],
    },
    {
      stepID: 14,
      stepName: "Post Order Change Response To NSO",
      data: [],
    },
    {
      stepID: 10,
      stepName: "Get Vehicle Pricing",
      data: [],
    },
    {
      stepID: 5,
      stepName: "Generate Check Digit",
      data: [],
    },
    {
      stepID: 11,
      stepName: "Publish Updates To Vehicle Hub",
      data: [],
    },
    {
      stepID: 12,
      stepName: "Publish Legacy Backfill",
      data: [],
    },
    {
      stepID: 13,
      stepName: "Publish Updates To RTO",
      data: [],
    },
  ],
  activeStep: 0,
  flowId: 0,
  loading: false,
  error: null,
};

const storedState: any = localStorage.getItem("simFlowState");
const initialState =
  storedState && JSON.parse(storedState).length > 0
    ? JSON.parse(storedState)
    : {
        simFlow: simFlowInitialState,
      };
export const simFlowFetchSimRequestData = createAsyncThunk(
  "simFlow/fetchSimRequestData",
  async ({ id, stepId }: { id: string; stepId: number }) => {
    if (!id || !stepId) {
      return { id: id, data: [], stepId: stepId };
    }
    const response = await fetchRequestResponseData(id, stepId.toString());
    return { id: id, data: response, stepId: stepId };
  },
);

export const simFlowSlice = createSlice({
  name: "simFlow",
  initialState: initialState,
  reducers: {
    simFlowFetchData: (state) => {
      state.simFlow.loading = true;
      state.simFlow.error = null;
    },
    simFlowFetchDataSuccess: (
      state,
      action: PayloadAction<{
        stepID: number;
        data: any[];
      }>,
    ) => {
      state.simFlow.data.forEach((step: any) => {
        if (step.stepID === action.payload.stepID) {
          step.data = action.payload.data;
        }
      });
      state.simFlow.loading = false;
      state.simFlow.error = null;
    },
    simFlowFetchDataFailure: (state, action: PayloadAction<any>) => {
      state.simFlow.loading = false;
      state.simFlow.error = action.payload;
    },
    simFlowSetActiveStep: (state, action: PayloadAction<number>) => {
      state.simFlow.activeStep = action.payload;
    },
    simFlowSetFlowId: (state, action: PayloadAction<number>) => {
      state.simFlow.flowId = action.payload;
    },
    simFlowStateReset: (state) => {
      state.simFlow = simFlowInitialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(simFlowFetchSimRequestData.pending, (state) => {
      state.simFlow.loading = true;
      state.simFlow.error = null;
    });
    builder.addCase(simFlowFetchSimRequestData.fulfilled, (state, action) => {
      state.simFlow.data.forEach((step: any) => {
        if (step.stepID === action.payload.stepId) {
          step.data = action.payload.data;
        }
      });
      state.simFlow.loading = false;
      state.simFlow.error = null;
    });
    builder.addCase(simFlowFetchSimRequestData.rejected, (state, action) => {
      state.simFlow.loading = false;
      state.simFlow.error = action.error;
    });
  },
});

export const {
  simFlowFetchData,
  simFlowFetchDataSuccess,
  simFlowFetchDataFailure,
  simFlowSetActiveStep,
  simFlowStateReset,
} = simFlowSlice.actions;

export const selectSimFlow = (state: RootState) => state.simFlow;

export const simFlowReducer = simFlowSlice.reducer;
simFlowSlice.reducer = (state: any, action: any) => {
  const newState = simFlowReducer(state, action);
  localStorage.setItem("simFlowState", JSON.stringify(newState));
  return newState;
};
export default simFlowSlice.reducer;
